import { create } from 'zustand';
import { GameState, SectorId, ActionType, GameEvent, EnemyIntent, MissionScenario, GameStats, PlayerRole } from '../types/game';
import { INITIAL_SECTORS, SCENARIOS } from '../data/sectors';
import { NetworkGraph } from '../utils/networkGraph';
import { validateILOEStart } from '../utils/iloeValidator';
import { useCapabilityStore } from './capabilityStore';
import { useAdjudicationStore } from './adjudicationStore';
import { CAPABILITY_DEFINITIONS } from '../utils/capabilityEffects';

export interface FeedbackMessage {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
}

interface GameStore extends GameState {
  reset: () => void;
  enterBriefing: () => void;
  isProcessingTurn: boolean;
  hasPlayedBefore: boolean;
  completedLevels: string[];
  hasSeenTutorial: boolean;
  setHasSeenTutorial: () => void;
  tutorialStep: number;
  isLocked: (scenarioId: string) => boolean;
  blockedAction: { reason: string; tip?: string } | null;
  clearBlockedAction: () => void;
  skipTutorial: () => void;
  startGame: (scenarioId: string, tutorial?: boolean) => void;
  nextTutorialStep: () => void;
  nextTurn: () => void;
  selectSector: (id: SectorId) => void;
  setRole: (role: PlayerRole) => void;
  tickTimer: () => void;
  performAction: (action: ActionType, context?: string) => void;
  endTurn: () => void;
  addEvent: (message: string, type: GameEvent['type']) => void;
  feedback: FeedbackMessage[];
  addFeedback: (message: string, type: FeedbackMessage['type']) => void;
  removeFeedback: (id: string) => void;
  getNetworkGraph: () => NetworkGraph;
  validatePlayerAction: (action: ActionType, context?: any) => { valid: boolean; reason?: string };
  
  // Capability Integration
  getPlacedCapabilitiesOn: (targetId: string) => any[];
  evaluateCapabilityInteractions: (action: ActionType, targetId: string) => { allowed: boolean; costMod: number; reason?: string };
}

const INITIAL_STATS: GameStats = {
  totalApSpent: 0,
  totalTrustGained: 0,
  actionsTaken: 0,
  successfulActions: 0,
  roiBySector: {},
  timeline: []
};

const TURN_DURATION = 60;

export const useGameStore = create<GameStore>((set, get) => ({
  turn: 1,
  maxTurns: 10,
  ap: 3,
  maxAp: 3,
  sectors: INITIAL_SECTORS,
  selectedSectorId: null,
  events: [],
  gameStatus: 'lobby',
  turnPhase: 'player',
  stats: INITIAL_STATS,
  isProcessingTurn: false,
  hasPlayedBefore: false,
  completedLevels: [],
  hasSeenTutorial: false,
  tutorialStep: 0,
  blockedAction: null,
  feedback: [],
  currentScenario: undefined,

  reset: () => {
    set({
      turn: 1,
      ap: 3,
      sectors: INITIAL_SECTORS,
      events: [],
      gameStatus: 'lobby',
      stats: INITIAL_STATS,
      selectedSectorId: null,
      currentScenario: undefined,
      // CRIT-03: must reset tutorial state so WelcomeModal re-appears on next deploy
      hasSeenTutorial: false,
      tutorialStep: 0,
      feedback: [],
      blockedAction: null,
    });
    useCapabilityStore.getState().reset();
    useAdjudicationStore.getState().startTurn();
  },

  enterBriefing: () => set({ gameStatus: 'briefing' }),

  isLocked: (scenarioId) => {
    const scenarioIndex = SCENARIOS.findIndex(s => s.id === scenarioId);
    if (scenarioIndex <= 0) return false;
    const prevScenarioId = SCENARIOS[scenarioIndex - 1].id;
    return !get().completedLevels.includes(prevScenarioId);
  },

  setHasSeenTutorial: () => set({ hasSeenTutorial: true }),

  clearBlockedAction: () => set({ blockedAction: null }),

  skipTutorial: () => {
    set({ 
      hasSeenTutorial: true,
      tutorialStep: 0  // CRITICAL: Must reset to 0 to dismiss modal
    });
  },

  startGame: (scenarioId, tutorial = false) => {
    const scenario = SCENARIOS.find(s => s.id === scenarioId);
    if (!scenario) return;

    set({
      gameStatus: 'playing',
      currentScenario: scenario,
      maxTurns: scenario.turnLimit,
      turn: 1,
      ap: 3,
      sectors: INITIAL_SECTORS,
      events: [{
        id: 'start',
        turn: 1,
        message: `Mission Started: ${scenario.name}`,
        type: 'info',
        timestamp: Date.now()
      }],
      hasSeenTutorial: !tutorial,
      tutorialStep: tutorial ? 1 : 0
    });
    
    useCapabilityStore.getState().initializeSlots(1);
    useAdjudicationStore.getState().startTurn();
  },

  nextTutorialStep: () => {
    set(state => ({ tutorialStep: state.tutorialStep + 1 }));
  },

  nextTurn: () => {
    set(state => ({
      turn: state.turn + 1,
      ap: state.maxAp,
      gameStatus: state.turn >= state.maxTurns ? 'debrief' : 'playing'
    }));
  },

  selectSector: (id) => set({ selectedSectorId: id }),
  setRole: (role) => {}, 

  tickTimer: () => {
    useAdjudicationStore.getState().tickTimer();
  },

  performAction: (actionType, context) => {
    const { selectedSectorId, addFeedback, ap, stats } = get();
    
    if (ap <= 0) {
      addFeedback('Not enough Action Points', 'error');
      return;
    }

    useAdjudicationStore.getState().submitAction('Blue', {
      type: actionType,
      targetId: selectedSectorId || 'unknown',
      params: { context }
    });

    // Trade_Capacity costs 2 AP (trading resources for capability slots)
    const apCost = actionType === 'Trade_Capacity' ? 2 : 1;

    set({ 
      ap: ap - apCost,
      stats: {
        ...stats,
        totalApSpent: stats.totalApSpent + 1,
        actionsTaken: stats.actionsTaken + 1
      },
      events: [
        ...get().events,
        {
          id: `evt-${Date.now()}`,
          turn: get().turn,
          message: `Action ${actionType} initiated on ${selectedSectorId}`,
          type: 'info',
          timestamp: Date.now()
        }
      ]
    });

    addFeedback(`Action ${actionType} submitted for adjudication`, 'success');
  },

  endTurn: () => {
    useAdjudicationStore.getState().beginAdjudication();
  },

  addEvent: (message, type) => {
    set(state => ({
      events: [{
        id: Math.random().toString(36).substr(2, 9),
        turn: state.turn,
        message,
        type,
        timestamp: Date.now()
      }, ...state.events]
    }));
  },

  addFeedback: (message, type) => {
    const id = Math.random().toString(36).substr(2, 9);
    set(state => ({
      feedback: [...state.feedback, { id, message, type, duration: 3000 }]
    }));
    setTimeout(() => get().removeFeedback(id), 3000);
  },

  removeFeedback: (id) => {
    set(state => ({
      feedback: state.feedback.filter(f => f.id !== id)
    }));
  },

  getNetworkGraph: () => {
    return new NetworkGraph([], []);
  },

  validatePlayerAction: (action, context) => {
    return { valid: true };
  },

  getPlacedCapabilitiesOn: (targetId) => {
    return useCapabilityStore.getState().getCapabilitiesAt(targetId);
  },

  evaluateCapabilityInteractions: (action, targetId) => {
    return { allowed: true, costMod: 0 };
  }
}));
